import { useEffect, useRef, useState } from 'react';
import { Check, Share2 } from 'lucide-react';
import { communityPostShareUrl, shareCommunityPost } from '../../lib/communityPostShare';

export function CommunityPostShare({ postId, title }: { postId: number; title: string }) {
  const [count, setCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [manual, setManual] = useState(false);
  const inFlight = useRef(false);
  const url = communityPostShareUrl(postId);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/api/community/share?postId=${postId}`, { signal: controller.signal })
      .then(async response => {
        if (!response.ok) return;
        const data = await response.json();
        if (!controller.signal.aborted && Number.isSafeInteger(data.shareCount)) {
          setCount(previous => Math.max(previous ?? 0, data.shareCount));
        }
      }).catch(() => {});
    return () => controller.abort();
  }, [postId]);

  async function share() {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setMessage('');
    setManual(false);
    try {
      const result = await shareCommunityPost(title, url);
      if (result.outcome === 'cancelled') return;
      if (result.outcome === 'manual') {
        setManual(true);
        setMessage('자동 복사가 어려워요. 아래 링크를 선택해 복사해주세요.');
        return;
      }
      setMessage(result.outcome === 'copied'
        ? '링크를 복사했어요. 함께 나누고 싶은 곳에 붙여넣어 주세요.'
        : result.copied
          ? '공유했어요. 링크도 복사해 두었으니 바로 붙여넣을 수 있어요.'
          : '공유했어요. 좋은 이야기를 함께 나눠주셔서 고마워요.');
      // Sharing remains successful even when analytics is temporarily unavailable.
      try {
        const response = await fetch('/api/community/share', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ postId }), signal: AbortSignal.timeout(6000),
        });
        if (response.ok) {
          const data = await response.json();
          if (Number.isSafeInteger(data.shareCount)) setCount(previous => Math.max(previous ?? 0, data.shareCount));
        }
      } catch { /* Never turn a successful share into an error. */ }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <section className="fe-community-post-share" aria-label="글 공유">
      <div className="fe-community-post-share-main">
        <div>
          <strong>함께 나누고 싶은 글인가요?</strong>
          <p>동료 선생님에게 이 이야기를 전해보세요.</p>
        </div>
        <button type="button" className="fe-community-button" disabled={busy} onClick={() => void share()}
          title="링크 복사와 기기 공유 완료 기준이며, 같은 환경의 반복 공유는 하루 한 번 집계해요.">
          <Share2 size={17} aria-hidden="true" /> 글 공유하기
          {count !== null && <span className="fe-community-share-count" aria-label={`공유 ${count.toLocaleString()}회`}>{count.toLocaleString()}</span>}
        </button>
      </div>
      <div role="status" aria-live="polite" className="fe-community-share-feedback">
        {message && <p>{!manual && <Check size={15} aria-hidden="true" />}{message}</p>}
      </div>
      {manual && <input className="fe-community-share-url" aria-label="공유할 글 링크" value={url} readOnly onFocus={event => event.target.select()} onClick={event => event.currentTarget.select()} />}
    </section>
  );
}
